// ImageProcessor component for handling map restaurant images
import { useEffect } from 'react';
import type { MapRef } from 'react-map-gl-v7';
import { type Restaurant } from '@discover-data/gql/getters/mapRestaurants';
import { discoverColors } from '@discover-styles/colors';
import { awsImage } from '@firsttable/functions';

const SIZE = 110;
const PLACEHOLDER_COLOR = discoverColors.gold.DEFAULT;
const SHADOW_CONFIG = {
  color: 'rgba(0, 0, 0, 0.3)',
  blur: 8,
  offsetX: 0,
  offsetY: 2,
  padding: 8,
};

function processHeroImage(heroImage: string | null | undefined): string {
  // Early return for empty images
  if (!heroImage) return '';

  // Return absolute URLs unchanged
  if (heroImage.startsWith('http')) return heroImage;

  // Determine appropriate filter
  const DEFAULT_QUALITY = 60;
  const filter = heroImage.toLowerCase().endsWith('.png')
    ? `filters:autojpg():quality(${DEFAULT_QUALITY})/`
    : `filters:quality(${DEFAULT_QUALITY})/`;

  return awsImage(heroImage, 70, 70, filter);
}

// Draw circular path for clipp`ing
function drawCircularPath(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.closePath();
}

// Draw placeholder
function drawPlaceholder(ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = PLACEHOLDER_COLOR;
  ctx.fillRect(0, 0, SIZE, SIZE);
}

// Calculate dimensions for maintaining aspect ratio
function calculateImageDimensions(aspectRatio: number) {
  if (aspectRatio >= 1) {
    // Wider than tall
    return {
      drawWidth: SIZE * aspectRatio,
      drawHeight: SIZE,
      offsetX: -(SIZE * aspectRatio - SIZE) / 2,
      offsetY: 0,
    };
  }
  // Taller than wide
  return {
    drawWidth: SIZE,
    drawHeight: SIZE / aspectRatio,
    offsetX: 0,
    offsetY: -(SIZE / aspectRatio - SIZE) / 2,
  };
}

// Draw content (either color or image)
function drawContent(ctx: CanvasRenderingContext2D, content: string | HTMLImageElement) {
  if (typeof content === 'string') {
    // Use color
    ctx.fillStyle = content;
    ctx.fillRect(0, 0, SIZE, SIZE);
    return;
  }

  // Handle image
  try {
    if (content.complete && content.naturalWidth > 0 && content.naturalHeight > 0) {
      const aspectRatio = content.naturalWidth / content.naturalHeight;
      const { drawWidth, drawHeight, offsetX, offsetY } = calculateImageDimensions(aspectRatio);
      ctx.drawImage(content, offsetX, offsetY, drawWidth, drawHeight);
    } else {
      drawPlaceholder(ctx);
    }
  } catch (error) {
    drawPlaceholder(ctx);
  }
}

// Helper function to create a circular canvas with shadow
function createCircularImageWithShadow(
  currentMapRef: MapRef,
  content: string | HTMLImageElement,
  borderColor: string,
  imageName: string
) {
  // Create primary canvas
  const mainCanvas = document.createElement('canvas');
  mainCanvas.width = SIZE;
  mainCanvas.height = SIZE;
  const mainCtx = mainCanvas.getContext('2d');
  if (!mainCtx) return;

  // Apply circular clipping
  mainCtx.save();
  drawCircularPath(mainCtx, SIZE / 2, SIZE / 2, SIZE / 2 - 4);
  mainCtx.clip();

  // Draw content
  drawContent(mainCtx, content);
  mainCtx.restore();

  // Draw border
  drawCircularPath(mainCtx, SIZE / 2, SIZE / 2, SIZE / 2 - 2);
  mainCtx.strokeStyle = borderColor;
  mainCtx.lineWidth = 4;
  mainCtx.stroke();

  // Shadow canvas
  const shadowSize = SIZE + SHADOW_CONFIG.padding;
  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = shadowSize;
  shadowCanvas.height = shadowSize;
  const shadowCtx = shadowCanvas.getContext('2d');
  if (!shadowCtx) return;

  // Apply shadow
  shadowCtx.shadowColor = SHADOW_CONFIG.color;
  shadowCtx.shadowBlur = SHADOW_CONFIG.blur;
  shadowCtx.shadowOffsetX = SHADOW_CONFIG.offsetX;
  shadowCtx.shadowOffsetY = SHADOW_CONFIG.offsetY;

  // Draw shadow
  drawCircularPath(shadowCtx, shadowSize / 2, shadowSize / 2 - 2, SIZE / 2);
  shadowCtx.fillStyle = 'rgba(0, 0, 0, 0.01)';
  shadowCtx.fill();

  // Combine images
  shadowCtx.drawImage(mainCanvas, SHADOW_CONFIG.padding / 2, SHADOW_CONFIG.padding / 2);

  // Add to map
  if (currentMapRef.hasImage(imageName)) {
    currentMapRef.updateImage(imageName, shadowCtx.getImageData(0, 0, shadowSize, shadowSize));
  } else {
    currentMapRef.addImage(imageName, shadowCtx.getImageData(0, 0, shadowSize, shadowSize), {
      pixelRatio: 2,
      sdf: false,
    });
  }
}

// Load image with promise-based approach
function loadImage(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;

    // Handle already cached images
    if (img.complete && img.naturalHeight !== 0) {
      resolve(img);
    }
  });
}

// Try loading with proxy if direct load fails
function loadWithProxy(imageUrl: string) {
  const proxyUrl = `/api/imageproxy?url=${encodeURIComponent(imageUrl)}`;
  return loadImage(proxyUrl);
}

// Function to create a placeholder circle when no image is available
function createPlaceholderCircle(currentMapRef: MapRef, borderColor: string, imageName: string) {
  createCircularImageWithShadow(currentMapRef, PLACEHOLDER_COLOR, borderColor, imageName);
}

// Load restaurant image with fallbacks
async function loadRestaurantImage(
  currentMapRef: MapRef,
  imageUrl: string,
  borderColor: string,
  imageName: string
) {
  // Use placeholder for empty URLs
  if (!imageUrl) {
    createPlaceholderCircle(currentMapRef, borderColor, imageName);
    return;
  }

  let imageContent: HTMLImageElement | string = PLACEHOLDER_COLOR;

  try {
    imageContent = await loadWithProxy(imageUrl);

    // Create image with whatever content we have (image or placeholder)
    createCircularImageWithShadow(currentMapRef, imageContent, borderColor, imageName);
  } catch (error) {
    // ignore failures (usually when the user closes the map modal while it's still busy loading images)
  }
}

export default function useLoadImages(
  mapRef: React.RefObject<MapRef>,
  mapLoaded: boolean,
  restaurants: Map<Restaurant['id'], Restaurant>
) {
  // Suppress the specific restaurant image missing warnings since we're manually loading the missing images
  useEffect(() => {
    // eslint-disable-next-line no-console
    const originalWarn = console.warn;
    // eslint-disable-next-line no-console
    console.warn = (...args) => {
      if (
        typeof args[0] === 'string' &&
        args[0].includes('restaurant-image-') &&
        args[0].includes('could not be loaded')
      ) {
        return;
      }
      // Pass through all other warnings
      originalWarn.apply(console, args);
    };

    return () => {
      // eslint-disable-next-line no-console
      console.warn = originalWarn;
    };
  }, []);

  const restaurantsSignature = Array.from(restaurants.keys()).join();
  useEffect(() => {
    const currentMapRef = mapRef.current;
    if (!currentMapRef || !mapLoaded) {
      return;
    }

    // Create the placeholder versions - these will be used when actual photos aren't available
    createPlaceholderCircle(currentMapRef, 'white', 'restaurant-image-placeholder-white');
    createPlaceholderCircle(
      currentMapRef,
      discoverColors.gold.DEFAULT,
      'restaurant-image-placeholder-gold'
    );

    // Clean up outdated restaurant images
    const existingImages = currentMapRef
      .listImages()
      .filter((img) => img.startsWith('restaurant-image-') && !img.includes('placeholder'));

    // Remove images for restaurants that are no longer in the list
    for (const imageId of existingImages) {
      const restaurantId = parseInt(imageId.replace('restaurant-image-', ''));
      if (!restaurants.has(restaurantId)) {
        if (currentMapRef.hasImage(imageId)) {
          currentMapRef.removeImage(imageId);
        }
      }
    }

    // Handle images on-demand when Mapbox requests them
    const handleMissingImage = (e: { id: string }) => {
      const missingImageId = e.id;

      // Check if this is a restaurant image
      if (
        missingImageId.startsWith('restaurant-image-') &&
        !missingImageId.includes('placeholder')
      ) {
        // Extract restaurant ID from the image name
        const restaurantId = parseInt(missingImageId.replace('restaurant-image-', ''));

        // Find the restaurant with this ID
        const restaurant = restaurants.get(restaurantId);

        if (restaurant && restaurant.heroImage) {
          // Get the processed image URL from our function
          const processedImageUrl = processHeroImage(restaurant.heroImage);

          // Load the image for this restaurant with the processed URL
          const borderColor = restaurant.offersFirstTable ? discoverColors.gold.DEFAULT : 'white';
          loadRestaurantImage(currentMapRef, processedImageUrl, borderColor, missingImageId);
        } else {
          // If we can't find the restaurant or it has no image, use a placeholder
          const borderColor = 'white'; // Default border color
          createPlaceholderCircle(currentMapRef, borderColor, missingImageId);
        }
      }
    };

    // Add event listeners
    currentMapRef.on('styleimagemissing', handleMissingImage);

    // Cleanup function
    return () => {
      if (currentMapRef) {
        currentMapRef.off('styleimagemissing', handleMissingImage);
      }
    };
  }, [mapRef, mapLoaded, restaurantsSignature]);
}
